import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createNewProduct } from '@/api/admin'
import { useSetToast } from '@/hooks'
import { Dropdown, FullscreenModal } from '@/components/global'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { Button, Input } from '@/components/priority'

export const CreateNewProductModal = ({ show }) => {
  const close = show[1]
  const [newProductName, setNewProductName] = useState('')
  const [thumbnailURL, setThumbnailURL] = useState('')
  const [selectedFoldLine, setSelectedFoldLine] = useState('')
  const [numPages, setNumPages] = useState('')
  const [dimensions, setDimensions] = useState({
    widthInches: '',
    heightInches: '',
  })
  const [pixels, setPixels] = useState({
    width: '',
    height: '',
  })
  const [padding, setPadding] = useState({
    top: '',
    right: '',
    bottom: '',
    left: '',
    centerHorizontal: '',
    centerVertical: '',
  })
  const [loading, setLoading] = useState(false)
  const setToast = useSetToast()
  const { t } = useTranslation()

  const foldLineOptions = [
    { id: 'none', name: 'None' },
    { id: 'vertical-center', name: 'Vertical-center' },
    { id: 'horizontal-center', name: 'Horizontal-center' },
  ]
  const updateObjectState = (setState) => (key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const resetStates = () => {
    setNewProductName('')
    setThumbnailURL('')
    setSelectedFoldLine('')
    setNumPages('')
    setDimensions({
      widthInches: '',
      heightInches: '',
    })
    setPixels({
      width: '',
      height: '',
    })
    setPadding({
      top: '',
      right: '',
      bottom: '',
      left: '',
      centerHorizontal: '',
      centerVertical: '',
    })
    setLoading(false)
  }

  const handleClose = () => {
    resetStates()
    close()
  }

  const handleSubmit = async () => {
    if (
      !newProductName ||
      !thumbnailURL ||
      !selectedFoldLine ||
      !numPages ||
      !dimensions.widthInches ||
      !dimensions.heightInches ||
      !pixels.width ||
      !pixels.height ||
      !isPaddingComplete
    )
      return

    setLoading(true)
    try {
      await createNewProduct({
        productName: newProductName,
        thumbnailURL,
        foldLine: selectedFoldLine,
        numberOfPages: parseInt(numPages, 10),
        widthInches: parseFloat(dimensions.widthInches),
        heightInches: parseFloat(dimensions.heightInches),
        widthPixels: parseFloat(pixels.width),
        heightPixels: parseFloat(pixels.height),
        padding: {
          top: parseFloat(padding.top) || 0,
          right: parseFloat(padding.right) || 0,
          bottom: parseFloat(padding.bottom) || 0,
          left: parseFloat(padding.left) || 0,
          centerHorizontal: parseFloat(padding.centerHorizontal) || 0,
          centerVertical: parseFloat(padding.centerVertical) || 0,
        },
      })

      setToast({
        icon: 'check',
        text: t('toast.productCreatedSuccessfully', { name: newProductName }),
      })

      handleClose()
    } catch (error) {
      console.error('Failed to create new product:', error)

      setToast({
        text: t('toast.productCreateFailed'),
      })

      setLoading(false)
    }
  }

  const handleCancel = () => {
    handleClose()
  }

  const areDimensionsComplete =
    dimensions.widthInches && dimensions.heightInches
  const isPixelsComplete = pixels.width && pixels.height
  const isPaddingComplete =
    padding.top !== '' &&
    padding.right !== '' &&
    padding.bottom !== '' &&
    padding.left !== ''

  const isDisabled =
    !newProductName ||
    !thumbnailURL ||
    !selectedFoldLine ||
    !numPages ||
    !areDimensionsComplete ||
    !isPixelsComplete ||
    !isPaddingComplete ||
    loading

  return (
    <FullscreenModal show={show} hideClose>
      <Flex
        column
        padding="32px"
        width="500px"
        justify="space-between"
        overflow-y="auto"
      >
        <Flex column>
          <Text size="24px" weight="600" margin="0 0 20px 0">
            Create a new product
          </Text>
          <Text margin="0 0 20px 0">
            Kindly follow the steps below to create a new product. Provide the
            relevant product details, then click Create Product.
          </Text>

          <Input
            width="100%"
            margin="0 0 24px 0"
            label="Product Name"
            value={[newProductName, setNewProductName]}
            placeholder="Enter new product name"
          />

          {newProductName && (
            <Input
              width="100%"
              margin="0 0 24px 0"
              label="Thumbnail URL"
              value={[thumbnailURL, setThumbnailURL]}
              placeholder="Enter thumbnail URL"
            />
          )}

          {newProductName && thumbnailURL && (
            <Dropdown
              width="100%"
              margin="0 0 24px 0"
              selected={[selectedFoldLine, setSelectedFoldLine]}
              placeholder="Select a fold line"
              items={foldLineOptions}
              label="Fold Line"
              valueKey="id"
              textKey="name"
              full={true}
            />
          )}

          {newProductName && thumbnailURL && selectedFoldLine && (
            <Input
              width="100%"
              margin="0 0 24px 0"
              label="Number of Pages"
              value={[numPages, setNumPages]}
              placeholder="Enter number of pages"
            />
          )}

          {newProductName && thumbnailURL && selectedFoldLine && numPages && (
            <Flex width="100%" margin="0 0 24px 0" justify="space-between">
              <Input
                width="48%"
                label="Width (inches)"
                value={[
                  dimensions.widthInches,
                  (value) =>
                    updateObjectState(setDimensions)('widthInches', value),
                ]}
                placeholder="Width in inches"
              />
              <Input
                width="48%"
                label="Height (inches)"
                value={[
                  dimensions.heightInches,
                  (value) =>
                    updateObjectState(setDimensions)('heightInches', value),
                ]}
                placeholder="Height in inches"
              />
            </Flex>
          )}

          {newProductName &&
            thumbnailURL &&
            selectedFoldLine &&
            numPages &&
            areDimensionsComplete && (
              <Flex width="100%" margin="0 0 24px 0" justify="space-between">
                <Input
                  width="48%"
                  label="Width (pixels)"
                  value={[
                    pixels.width,
                    (value) => updateObjectState(setPixels)('width', value),
                  ]}
                  placeholder="Width in Pixels"
                />
                <Input
                  width="48%"
                  label="Height (pixels)"
                  value={[
                    pixels.height,
                    (value) => updateObjectState(setPixels)('height', value),
                  ]}
                  placeholder="Height in Pixels"
                />
              </Flex>
            )}

          {newProductName &&
            thumbnailURL &&
            selectedFoldLine &&
            numPages &&
            areDimensionsComplete &&
            isPixelsComplete && (
              <>
                <Text size="16px" weight="500" margin="0 0 12px 0">
                  Padding
                </Text>
                <Flex width="100%" margin="0 0 24px 0" justify="space-between">
                  <Input
                    width="23%"
                    label="Top"
                    value={[
                      padding.top,
                      (value) => updateObjectState(setPadding)('top', value),
                    ]}
                    placeholder="Top"
                  />
                  <Input
                    width="23%"
                    label="Right"
                    value={[
                      padding.right,
                      (value) => updateObjectState(setPadding)('right', value),
                    ]}
                    placeholder="Right"
                  />
                  <Input
                    width="23%"
                    label="Bottom"
                    value={[
                      padding.bottom,
                      (value) => updateObjectState(setPadding)('bottom', value),
                    ]}
                    placeholder="Bottom"
                  />
                  <Input
                    width="23%"
                    label="Left"
                    value={[
                      padding.left,
                      (value) => updateObjectState(setPadding)('left', value),
                    ]}
                    placeholder="Left"
                  />
                </Flex>
                <Flex width="100%" margin="0 0 24px 0" justify="space-between">
                  <Input
                    width="48%"
                    label="Horizontal Center"
                    value={[
                      padding.centerHorizontal,
                      (value) =>
                        updateObjectState(setPadding)(
                          'centerHorizontal',
                          value,
                        ),
                    ]}
                    placeholder="Horizontal Center"
                  />
                  <Input
                    width="48%"
                    label="Vertical Center"
                    value={[
                      padding.centerVertical,
                      (value) =>
                        updateObjectState(setPadding)('centerVertical', value),
                    ]}
                    placeholder="Vertical Center"
                  />
                </Flex>
              </>
            )}
        </Flex>
        <Flex justify="flex-end" margin="24px 0 0 0">
          <Button margin="0 16px 0 0" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            primary
            left
            margin="0 12px 0 0"
            onClick={handleSubmit}
            disabled={isDisabled}
            opacity={isDisabled ? 0.5 : 1}
            pointerEvents={isDisabled ? 'none' : 'auto'}
          >
            {loading ? 'Creating...' : `Create ${newProductName}`}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}
